import {useEffect, useMemo, useState} from 'react';
import {Send} from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@kroll-resolver-crisp/ui-components-react';
import type {ApprovedWorkflow, GroundingPreview} from '../../api/workflows';
import {entryLabel, stageCount, type Stage} from './workflowUi';

type Change = {
  key: string;
  type: 'added' | 'changed' | 'retired';
  label: string;
  before: string | null;
  after: string | null;
  concept: string;
};

const TYPE_LABEL = {added: 'Added', changed: 'Changed', retired: 'Retired'} as const;

export function StageSheet({
  open,
  onClose,
  workflow,
  stage,
  preview,
  busy,
  onSubmit
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly workflow: ApprovedWorkflow;
  readonly stage: Stage;
  readonly preview: GroundingPreview | null;
  readonly busy: boolean;
  readonly onSubmit: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if (open) {
      setConfirmed(false);
    }
  }, [open]);

  const counts = stageCount(stage);
  const changes = useMemo<Change[]>(() => {
    const path = (id: string) => {
      const full = workflow.nodes.find((n) => n.concept_id === id)?.path ?? '';
      const parts = full.split(' / ');
      return parts.length > 1 ? parts.slice(1).join(' / ') : full;
    };

    const rows: Change[] = [];
    for (const entry of workflow.grounding) {
      if (stage.retired[entry.id]) {
        rows.push({
          key: entry.id,
          type: 'retired',
          label: entryLabel(entry),
          before: entry.content,
          after: null,
          concept: path(entry.concept_id)
        });
      } else if (stage.edits[entry.id] !== undefined) {
        rows.push({
          key: entry.id,
          type: 'changed',
          label: entryLabel(stage.edits[entry.id] ?? entry),
          before: entry.content,
          after: stage.edits[entry.id]?.content ?? entry.content,
          concept: path(entry.concept_id)
        });
      }
    }

    for (const added of stage.added) {
      rows.push({
        key: added.tmp,
        type: 'added',
        label: entryLabel(added),
        before: null,
        after: added.content,
        concept: path(added.concept_id)
      });
    }

    return rows;
  }, [workflow, stage]);

  const destructive = counts.changed > 0 || counts.retired > 0;
  const errors = preview?.errors ?? [];
  const blocked =
    busy || !preview || errors.length > 0 || counts.total === 0 || (destructive && !confirmed);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}>
      <SheetContent className="w-[min(720px,100vw)] max-w-[100vw] sm:max-w-[720px]">
        <SheetHeader>
          <SheetTitle>Submit grounding changes</SheetTitle>
          <SheetDescription>
            {workflow.workflow.name} · RD keeps serving the current grounding until another approver
            publishes this.
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4">
          <div className="grid grid-cols-3 gap-2">
            {(['added', 'changed', 'retired'] as const).map((type) => (
              <div key={type} className="bg-card border-border rounded-lg border p-3">
                <span className="block text-2xl font-semibold tabular-nums">{counts[type]}</span>
                <span className="text-muted-foreground text-xs">{TYPE_LABEL[type]}</span>
              </div>
            ))}
          </div>
          {errors.length > 0 ? (
            <ul className="text-destructive m-0 list-disc pl-5 text-sm">
              {errors.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
          <div className="border-border overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Change</TableHead>
                  <TableHead className="w-28">Kind</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead className="w-[28%]">Concept</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {changes.map((change) => (
                  <TableRow key={change.key}>
                    <TableCell className="align-top">
                      <Badge variant={change.type === 'retired' ? 'destructive' : 'secondary'}>
                        {TYPE_LABEL[change.type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="secondary">{change.label}</Badge>
                    </TableCell>
                    <TableCell className="align-top whitespace-pre-wrap">
                      <span className="flex flex-col gap-1">
                        {change.before === null ? null : (
                          <del className="text-muted-foreground">{change.before}</del>
                        )}
                        {change.after === null ? null : (
                          <ins className="no-underline">{change.after}</ins>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground align-top text-xs">
                      {change.concept}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {destructive ? (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={confirmed}
                onCheckedChange={(value) => {
                  setConfirmed(Boolean(value));
                }}
              />
              <span>
                I’ve checked these.{' '}
                {counts.retired > 0
                  ? `${counts.retired} entr${counts.retired === 1 ? 'y' : 'ies'} will stop being served`
                  : 'Edited entries replace the published text'}{' '}
                once approved.
              </span>
            </label>
          ) : null}
        </div>
        <div className="border-border flex justify-end gap-2 border-t p-4">
          <Button variant="secondary" onClick={onClose}>
            Keep editing
          </Button>
          <Button disabled={blocked} onClick={onSubmit}>
            <Send aria-hidden className="size-3.5" />
            Submit for approval
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
